import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Target, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";

interface SessionGoalDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSetGoal: (goal: string, suggestedGoals?: string[]) => void;
  mode: string;
}

export const SessionGoalDialog: React.FC<SessionGoalDialogProps> = ({ 
  isOpen, 
  onClose, 
  onSetGoal,
  mode 
}) => {
  const [customGoal, setCustomGoal] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null);

  const suggestedGoals = {
    general: [
      "Process a difficult emotion",
      "Gain clarity on a decision",
      "Practice self-compassion"
    ],
    cbt: [
      "Identify unhelpful thought patterns",
      "Challenge a core belief",
      "Plan a behavioral experiment"
    ],
    psychotherapy: [
      "Explore a repeating pattern",
      "Understand childhood influences",
      "Work through resistance"
    ],
    qsp: [
      "Connect past event to present pattern",
      "Release old emotional charge",
      "Reprogramme limiting belief into empowering truth"
    ]
  };

  const goals = suggestedGoals[mode as keyof typeof suggestedGoals] || suggestedGoals.general;

  const handleSetGoal = () => {
    const finalGoal = selectedSuggestion || customGoal;
    if (finalGoal.trim()) {
      onSetGoal(finalGoal, selectedSuggestion ? goals : undefined);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            Set Your Session Intention
          </DialogTitle>
          <DialogDescription>
            What would you like to focus on today? This helps guide our conversation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              Suggested for {mode.toUpperCase()} Mode:
            </p>
            <div className="space-y-2">
              {goals.map((goal, index) => (
                <Card
                  key={index}
                  className={`p-3 cursor-pointer transition-all ${
                    selectedSuggestion === goal
                      ? 'ring-2 ring-primary bg-primary/5'
                      : 'hover:border-primary/50'
                  }`}
                  onClick={() => {
                    setSelectedSuggestion(goal);
                    setCustomGoal('');
                  }}
                >
                  <p className="text-sm text-foreground">{goal}</p>
                </Card>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Or write your own:</p>
            <Textarea
              value={customGoal}
              onChange={(e) => {
                setCustomGoal(e.target.value);
                setSelectedSuggestion(null);
              }}
              placeholder="What matters most to you today?"
              className="min-h-[80px]"
            />
          </div>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Skip
          </Button>
          <Button 
            onClick={handleSetGoal} 
            disabled={!selectedSuggestion && !customGoal.trim()}
            className="flex-1"
          >
            Start Session
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
